"use client";

import { AnimatedFeatures } from "@/components/global-ui/animated-feature";
import { useMediaQuery } from "usehooks-ts";
interface FeatureConfig {
  nameIndex: number;
  descriptionIndex: number;
  videoIndex?: number;
  bannerImageIndex?: number;
}
interface Stage1FeaturesProps {
  titleIndex?: number;
  descriptionIndex?: number;
  subtitleIndex?: number;
  features?: FeatureConfig[];
  buttonText?: string;
  buttonLink?: string;
}
export function Stage1Features({
  titleIndex = 1,
  descriptionIndex = 4,
  subtitleIndex = 5,
  features = [{
    nameIndex: 2,
    descriptionIndex: 2,
    videoIndex: 1,
    bannerImageIndex: 1
  },
  // Viral Names with viral name demo
  {
    nameIndex: 3,
    descriptionIndex: 7,
    videoIndex: 2,
    bannerImageIndex: 2
  },
  // Logo Studio with logo demo
  {
    nameIndex: 4,
    descriptionIndex: 14,
    videoIndex: 3,
    bannerImageIndex: 3
  },
  // Know Your Audience with persona demo
  {
    nameIndex: 6,
    descriptionIndex: 15,
    videoIndex: 4,
    bannerImageIndex: 10
  } // Customer Persona Builder with persona demo
  ],
  // buttonText = "Get Launch-Ready",
  buttonText = "Start for $0",
  buttonLink = "/signup"
}: Stage1FeaturesProps) {
  // Check if device is large (lg breakpoint = 1024px and above)
  const isLargeDevice = useMediaQuery("(min-width: 1024px)");

  // Available feature names
  const featureNames: {
    [key: number]: string;
  } = {
    1: "Viral Names",
    2: "Domain Name Finder",
    3: "Logo Studio",
    4: "Persona Builder",
    5: "Customer Persona Builder",
    6: "Skip the agency. Save $2K+",
    7: "Skip the branding maze",
    8: "Build a brand in 30 minutes",
    9: "Look legit from day one"
  };

  // Available feature descriptions
  const featureDescriptions: {
    [key: number]: string;
  } = {
    // Domain-related descriptions (1-4)
    1: "Find punchy, brandable domains that sound funded - with realtime availability checks.",
    2: "Skip the weeks of brainstorming bad names. Get 20+ brandable domain names in seconds, with real-time availability checks.",
    3: "Get a memorable domain in 60 seconds.",
    4: "Find brandable domains in seconds — with real-time availability checks.",
    // Logo-related descriptions (5-9)
    5: "Professional logos without the $2,000 designer fee.",
    6: "Professional logos without the designer price tag. Generate multiple logo concepts, pick your colors, remove backgrounds automatically.",
    7: "Professional logos without the designer price tag. Generate modern, sleek logos based on your brand description.",
    8: "Logos that look like you paid a designer $500. Made in 2 minutes.",
    9: "Create modern, clean logos with zero design skills.",
    // Audience/Persona-related descriptions (10-14)
    10: "Generate a detailed customer persona with pain points, goals and buying triggers - so you create content that converts.",
    11: "Input product details & get complete customer persona with pain points, goals, and where to find them online.",
    12: "Skip the guesswork. Get a detailed persona that actually buys.",
    13: "Pinpoint who actually needs your product.",
    14: "Build a hyper-specific persona based on your product and pain point — not guesswork.",
    // CTA section (15-18)
    15: "Save $2K+ in branding costs with tools you can use in 5 minutes. Name, logo, audience, done!",
    16: "Your MVP deserves better than a placeholder logo and .xyz domain.",
    17: "Looks pro. Sounds sharp. Starts conversations. Without hiring a $5K branding agency.",
    18: "No design hires. No branding agency. Just launch-ready assets in 30 minutes."
  };

  // Available titles
  const titles: {
    [key: number]: string;
  } = {
    1: "Stage 1: Foundation (Get Launch-Ready Fast)",
    2: "STAGE 1: Brand in a Box",
    3: "STAGE 1: Blank Page to Branded"
  };

  // Available descriptions
  const descriptions: {
    [key: number]: string;
  } = {
    1: "Most founders waste weeks just on naming, branding, and finding their audience. ViralLaunch compresses this into hours with AI-powered tools that give you professional results instantly.",
    2: "Stop wasting weeks on branding. Get everything you need in one afternoon.",
    3: "The identity setup most founders delay — done in minutes.",
    4: "A brandable name and logo can earn your users' trust, but most founders waste weeks stressing over this. With ViralLaunch, you'll be done by lunch."
  };

  // Available subtitles/CTAs
  const subtitles: {
    [key: number]: string;
  } = {
    1: "Save $2K+ in branding costs with tools you can use in 5 minutes.",
    2: "Your MVP deserves better than a placeholder logo and .xyz domain.",
    3: "Build a brand in 30 minutes. Looks pro. Sounds sharp. Starts conversations. Without hiring a $5K branding agency.",
    4: "Look legit from day one. No design hires. No branding agency. Just launch-ready assets in 30 minutes.",
    5: "Stop wasting weeks on basics. Get your foundation right from day one."
  };

  // Available video URLs - conditional based on device size
  const videoUrls: {
    [key: number]: string;
  } = {
    1: isLargeDevice ? "https://viral.devassets.app/hls_viral_name/desktop.m3u8" : "https://viral.devassets.app/hls_viral_name/master.m3u8",
    2: isLargeDevice ? "https://viral.devassets.app/hls_logo/desktop.m3u8" : "https://viral.devassets.app/hls_logo/master.m3u8",
    3: isLargeDevice ? "https://viral.devassets.app/hls_persona/desktop.m3u8" : "https://viral.devassets.app/hls_persona/master.m3u8",
    4: isLargeDevice ? "https://viral.devassets.app/hls_logo/desktop.m3u8" : "https://viral.devassets.app/hls_logo/master.m3u8"
  };

  // Available still image URLs for video placeholders
  const stillImageUrls: {
    [key: number]: string;
  } = {
    1: "https://viral.devassets.app/stills/viral_name_still.png",
    2: "https://viral.devassets.app/stills/viral_logo_still.png",
    3: "https://viral.devassets.app/stills/viral_persona_still.png",
    4: "https://viral.devassets.app/stills/viral_logo_still.png" // Reusing logo still for 4th feature
  };

  // Available banner image URLs from demos folder
  const bannerImageUrls: {
    [key: number]: string;
  } = {
    1: "/images/demos/viral-name-demo.png",
    2: "/images/demos/logo-generator-demo.png",
    3: "/images/demos/persona-generator-demo.png",
    4: "/images/demos/brand-voice-extractor-demo.png",
    5: "/images/demos/content-hub-demo.png",
    6: "/images/demos/launch-pad-demo.png",
    7: "/images/demos/promo-video-demo.png",
    8: "/images/demos/smart-edit-demo.png",
    9: "/images/demos/viral-giveaway-demo.png",
    10: "/images/demos/section-1.png",
    11: "/images/demos/section-2.png",
    12: "/images/demos/section-3.png"
  };

  // Build features array based on configuration
  const configuredFeatures = features.map((featureConfig, index) => ({
    id: index + 1,
    name: featureNames[featureConfig.nameIndex] || `Feature ${featureConfig.nameIndex}`,
    description: featureDescriptions[featureConfig.descriptionIndex] || `Description ${featureConfig.descriptionIndex}`,
    video: videoUrls[featureConfig.videoIndex || 1],
    stillImage: stillImageUrls[featureConfig.videoIndex || 1],
    bannerImage: bannerImageUrls[featureConfig.bannerImageIndex || 1],
    // Add button to last feature
    ...(index === features.length - 1 && {
      buttonText,
      buttonLink
    })
  }));
  return <AnimatedFeatures title={titles[titleIndex] || titles[1]} description={descriptions[descriptionIndex] || descriptions[1]} subtitle={subtitles[subtitleIndex] || subtitles[1]} features={configuredFeatures} autoRotateInterval={12000} // Increased from 6000 to reduce memory pressure
  data-sentry-element="AnimatedFeatures" data-sentry-component="Stage1Features" data-sentry-source-file="stage-1-features.tsx" />;
}